// src/api/refresh.ts
import type {
  RefreshRequest,
  RefreshResponse,
  EquityCurveForSetRequest,
  EquityCurveForSetResponse,
} from "../state/types";
import { getAuthHeaders, triggerUnauthorized } from "../auth/token";

const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

function authHeaders(): Record<string, string> {
  return { "Content-Type": "application/json", ...getAuthHeaders() };
}

export async function callRefresh(request: RefreshRequest): Promise<RefreshResponse> {
  const start = performance.now();

  const res = await fetch("/refresh", {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(request),
  });

  if (res.status === 401) {
    triggerUnauthorized();
    throw new Error("Session expired or invalid. Please sign in again.");
  }

  if (!res.ok) {
    const elapsedMs = performance.now() - start;
    console.log(`[API] POST /refresh failed in ${elapsedMs.toFixed(2)} ms`);
    const text = await res.text();
    let message: string;
    try {
      const body = JSON.parse(text) as { detail?: string; message?: string };
      message = body.detail ?? body.message ?? text;
      if (Array.isArray(message)) message = message.join(" ");
    } catch {
      message = text || `Refresh failed (${res.status})`;
    }
    throw new Error(message);
  }

  const data = (await res.json()) as { job_id?: string; [key: string]: unknown };

  // Backend returns 202 with job_id; poll until complete (avoids gateway timeout on long-running refresh)
  if (res.status === 202 && data.job_id) {
    const jobId = data.job_id as string;
    const pollDeadline = start + POLL_TIMEOUT_MS;
    while (true) {
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      if (performance.now() > pollDeadline) {
        throw new Error("Refresh timed out while waiting for the server.");
      }
      const statusRes = await fetch(`/refresh/status/${jobId}`, {
        headers: getAuthHeaders(),
      });
      if (statusRes.status === 401) {
        triggerUnauthorized();
        throw new Error("Session expired or invalid. Please sign in again.");
      }
      if (!statusRes.ok) {
        throw new Error(`Refresh status failed (${statusRes.status}).`);
      }
      const status = (await statusRes.json()) as {
        status: string;
        response?: RefreshResponse;
        error?: string;
      };
      if (status.status === "complete") {
        if (status.error) throw new Error(status.error);
        if (status.response) {
          const elapsedMs = performance.now() - start;
          console.log(`[API] POST /refresh (async) completed in ${elapsedMs.toFixed(2)} ms`);
          return status.response;
        }
        throw new Error("Refresh completed but no response returned.");
      }
    }
  }

  const elapsedMs = performance.now() - start;
  console.log(`[API] POST /refresh completed in ${elapsedMs.toFixed(2)} ms`);
  return data as RefreshResponse;
}

export async function callEquityCurveForSet(
  request: EquityCurveForSetRequest
): Promise<EquityCurveForSetResponse> {
  const res = await fetch("/refresh/equity-curve", {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(request),
  });

  if (res.status === 401) {
    triggerUnauthorized();
    throw new Error("Session expired or invalid. Please sign in again.");
  }

  if (!res.ok) {
    const text = await res.text();
    let message: string;
    try {
      const body = JSON.parse(text) as { detail?: string; message?: string };
      message = body.detail ?? body.message ?? text;
      if (Array.isArray(message)) message = message.join(" ");
    } catch {
      message = text || `Equity curve request failed (${res.status})`;
    }
    throw new Error(message);
  }

  return res.json() as Promise<EquityCurveForSetResponse>;
}